// web/src/screens/admin/games_edit.ts — v3.2 (migration 0038).
//
// Game edit form + Publish/Unpublish/Delete + embedded Cards panel.
// v3.2 simplifies authoring: Title and Intro phrase are plain text inputs
// (no Text/TextSet indirection), and "Default prompt(s)" was renamed to
// "Default task instruction(s)" with an updated tip.

import { renderAdminShell, showAdminToast } from "./admin_shell.js";
import { adminGet, adminPost, adminDelete, AdminError, getSTTPresets } from "../../api/admin.js";
import { renderCardsPanel } from "./cards_list.js";
import {
  mountSwitchableTextSetWidget,
  type SwitchableTextSetHandle,
  type SwitchableTextSetOption,
} from "./switchable_textset.js";
import { saveWithSharedEditConfirm } from "./shared_edit_confirm.js";
import {
  USER_PROMPT_VARS, promptVarsInfoBox,
  mountOverridePromptField, makePromptTextarea, type OverridePromptHandle,
} from "./prompt_vars.js";
import type {
  AdminGameDTO, AdminPublishError, AdminRouteProps, AdminSTTPresetDTO,
  AdminTextSetDTO,
} from "../../types/api.js";

function navigateAdmin(path: string): void {
  history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

// OverrideSTTEnginesHandle is the public surface of the "Override STT
// engines" widget — checkbox-gated checkbox list mirroring the wire
// semantics of game.stt_engines (empty array means "inherit from
// config.stt_presets"; non-empty means the explicit override). The
// widget renders the available presets in registry order.
interface OverrideSTTEnginesHandle {
  setValue(stored: string[] | null | undefined): void;
  value(): string[];
}

// mountOverrideSTTEnginesField builds the Override-checkbox + checkbox-list
// widget into host. Pattern mirrors mountOverridePromptField from
// prompt_vars.ts — outer checkbox toggles an inner div via
// display: none/flex; the inner div holds one row per preset.
//
// Unknown codes in `stored` (e.g. legacy / removed registry entries) keep
// the override checkbox engaged but are not rendered as rows — the
// registry is the authoritative source of legal codes.
function mountOverrideSTTEnginesField(
  host: HTMLElement,
  presets: AdminSTTPresetDTO[],
): OverrideSTTEnginesHandle {
  host.replaceChildren();
  host.setAttribute("style", "display: flex; flex-direction: column; gap: 6px;");

  const cbLabel = document.createElement("label");
  cbLabel.setAttribute("style", "display: flex; align-items: center; gap: 8px; font-size: 13px; color: #374151; font-weight: 500; cursor: pointer;");
  const overrideCB = document.createElement("input");
  overrideCB.type = "checkbox";
  const cbText = document.createElement("span");
  cbText.textContent = "Override STT engines (inherit from Settings when off)";
  cbLabel.append(overrideCB, cbText);
  host.appendChild(cbLabel);

  const inner = document.createElement("div");
  inner.setAttribute("style", "display: none; flex-direction: column; gap: 4px; padding-left: 24px;");
  host.appendChild(inner);

  const presetCBs = new Map<string, HTMLInputElement>();
  for (const p of presets) {
    const row = document.createElement("label");
    row.setAttribute("style", "display: flex; align-items: center; gap: 6px; font-size: 13px; color: #374151; cursor: pointer;");
    const cb = document.createElement("input");
    cb.type = "checkbox";
    cb.value = p.code;
    const text = document.createElement("span");
    text.textContent = p.label;
    row.append(cb, text);
    inner.appendChild(row);
    presetCBs.set(p.code, cb);
  }

  function applyVisibility(): void {
    inner.style.display = overrideCB.checked ? "flex" : "none";
  }
  overrideCB.addEventListener("change", applyVisibility);

  return {
    setValue(stored): void {
      const arr = stored ?? [];
      overrideCB.checked = arr.length > 0;
      for (const [code, cb] of presetCBs) {
        cb.checked = arr.includes(code);
      }
      applyVisibility();
    },
    value(): string[] {
      if (!overrideCB.checked) return [];
      const out: string[] = [];
      for (const [code, cb] of presetCBs) {
        if (cb.checked) out.push(code);
      }
      return out;
    },
  };
}

function field(parent: HTMLElement, label: string, input: HTMLElement, tip?: string): void {
  const wrap = document.createElement("label");
  wrap.setAttribute("style", "display: flex; flex-direction: column; gap: 4px; font-size: 13px; color: #374151;");
  const lab = document.createElement("span");
  lab.textContent = label;
  lab.setAttribute("style", "font-weight: 500;");
  wrap.appendChild(lab);
  wrap.appendChild(input);
  if (tip) {
    const tipEl = document.createElement("span");
    tipEl.textContent = tip;
    tipEl.setAttribute("style", "font-size: 11px; color: #6b7280; font-style: italic;");
    wrap.appendChild(tipEl);
  }
  parent.appendChild(wrap);
}

function makeInput(type: string, value?: string | null): HTMLInputElement {
  const el = document.createElement("input");
  el.type = type;
  el.value = value ?? "";
  el.setAttribute("style", "padding: 6px 10px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 14px;");
  return el;
}

function diffPatch(
  orig: AdminGameDTO,
  shortCode: string,
  sequence: string,
  title: string,
  themeColor: string,
  introPhrase: string,
  defaultMaxTries: string,
  sttEngines: string[],
  defaultTaskInstruction: {
    mode: "shared" | "non_shared";
    shortCode?: string;
    members?: string[];
  },
  systemPromptOverride: string,
  userPromptOverride: string,
  openaiSttPromptOverride: string,
  description: string,
): Partial<AdminGameDTO> {
  const patch: Partial<AdminGameDTO> = {};
  if (shortCode !== (orig.short_code ?? "")) {
    patch.short_code = shortCode;
  }
  const seqNew = sequence.trim() === "" ? 0 : Number(sequence);
  if (Number.isFinite(seqNew) && seqNew !== (orig.sequence ?? 0)) {
    patch.sequence = seqNew;
  }
  if (title !== (orig.title ?? "")) {
    patch.title = title;
  }
  if (themeColor !== (orig.theme_color ?? "")) {
    patch.theme_color = themeColor;
  }
  if (introPhrase !== (orig.intro_phrase ?? "")) {
    patch.intro_phrase = introPhrase;
  }
  const maxTriesNew = defaultMaxTries === "" ? null : Number(defaultMaxTries);
  if ((orig.default_max_tries ?? null) !== maxTriesNew) {
    patch.default_max_tries = maxTriesNew;
  }
  if ((orig.llm_system_prompt ?? "") !== systemPromptOverride) {
    patch.llm_system_prompt = systemPromptOverride;
  }
  if ((orig.llm_user_prompt ?? "") !== userPromptOverride) {
    patch.llm_user_prompt = userPromptOverride;
  }
  if ((orig.openai_stt_prompt ?? "") !== openaiSttPromptOverride) {
    patch.openai_stt_prompt = openaiSttPromptOverride;
  }
  if ((orig.description ?? "") !== description) {
    patch.description = description;
  }
  const sttOrig = orig.stt_engines ?? [];
  if (JSON.stringify(sttEngines) !== JSON.stringify(sttOrig)) {
    patch.stt_engines = sttEngines;
  }
  const origSC = orig.default_task_instruction_textset_short_code ?? null;
  const origMembers = orig.default_task_instruction_members ?? [];
  if (defaultTaskInstruction.mode === "shared") {
    const newSC = defaultTaskInstruction.shortCode ?? "";
    if (newSC !== (origSC ?? "")) {
      patch.default_task_instruction_textset_short_code = newSC;
    }
  } else {
    const newMembers = defaultTaskInstruction.members ?? [];
    const membersChanged = JSON.stringify(newMembers) !== JSON.stringify(origMembers);
    const wasShared = !!origSC;
    if (wasShared || membersChanged) {
      patch.default_task_instruction_members = newMembers;
      // Disconnect from the shared named TextSet (server reads members + ""
      // short_code as "create a fresh anonymous set" rather than editing the
      // shared members in place).
      if (wasShared) {
        patch.default_task_instruction_textset_short_code = "";
      }
    }
  }
  return patch;
}

function renderPublishErrors(target: HTMLElement, env: AdminPublishError): void {
  target.replaceChildren();
  const heading = document.createElement("h3");
  heading.textContent = `Publish failed (${env.errors.length} issue${env.errors.length === 1 ? "" : "s"})`;
  heading.setAttribute("style", "margin: 0 0 8px 0; font-size: 14px; color: #991b1b;");
  target.appendChild(heading);
  const ul = document.createElement("ul");
  ul.setAttribute("style", "margin: 0; padding-left: 20px; font-size: 13px; color: #374151;");
  for (const item of env.errors) {
    const li = document.createElement("li");
    const parts: string[] = [];
    if (item.game) parts.push(`game=${item.game}`);
    if (item.slot) parts.push(`slot=${item.slot}`);
    if (item.ref) parts.push(`ref=${item.ref}`);
    if (item.reason) parts.push(`reason=${item.reason}`);
    li.textContent = parts.join("  ·  ");
    ul.appendChild(li);
  }
  target.appendChild(ul);
}

export function mount(root: HTMLElement, props?: unknown): () => void {
  const p = props as AdminRouteProps | undefined;
  const gameShortCode = p && p.kind === "admin-game-edit" ? p.gameShortCode : "";

  const main = renderAdminShell(root, [
    { label: "Games", href: "/admin/games" },
    { label: `Game: ${gameShortCode || "?"}` },
  ]);

  const status = document.createElement("p");
  status.textContent = "Loading…";
  status.setAttribute("style", "color: #6b7280; font-size: 14px;");
  main.appendChild(status);

  const form = document.createElement("form");
  form.setAttribute("style", "display: none; flex-direction: column; gap: 12px; max-width: 720px; background: white; padding: 16px; border-radius: 6px; border: 1px solid #e5e7eb;");
  main.appendChild(form);

  const errorBox = document.createElement("div");
  errorBox.setAttribute("style", "margin-top: 12px; padding: 12px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; display: none;");
  main.appendChild(errorBox);

  const cardsContainer = document.createElement("section");
  cardsContainer.setAttribute("style", "margin-top: 24px; max-width: 720px;");
  main.appendChild(cardsContainer);

  // Inputs
  const shortCodeInput = makeInput("text");
  shortCodeInput.readOnly = true;
  shortCodeInput.title = "short_code is immutable";
  shortCodeInput.setAttribute("style", "padding: 6px 10px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 14px; background: #f3f4f6; color: #6b7280;");
  const sequenceInput = makeInput("number");
  sequenceInput.min = "1";
  sequenceInput.step = "1";
  const titleInput = makeInput("text");
  const themeColorInput = makeInput("color", "#5E3FBE");
  const introPhraseInput = makeInput("text");
  const defaultMaxTriesInput = makeInput("number");
  const defaultTaskInstructionHost = document.createElement("div");

  field(form, "Short code", shortCodeInput);
  field(form, "Picker sequence (blank = unsequenced, sorts last)", sequenceInput);
  field(form, "Title", titleInput, "Displayed on game start screen");
  field(form, "Theme color", themeColorInput);
  field(form, "Intro phrase", introPhraseInput, "Spoken to kid during the game start screen");
  field(form, "Default max tries", defaultMaxTriesInput);
  field(form, "Default task instruction(s)", defaultTaskInstructionHost,
    "Spoken to kid upon starting a task. If multiple are set, one will be selected at random");

  // Description
  const descriptionTA: HTMLTextAreaElement = makePromptTextarea("", 4);
  field(form, "Description for LLM prompt", descriptionTA);

  // System / user prompt overrides
  const llmSystemPromptW: OverridePromptHandle = mountOverridePromptField(
    "Override LLM system prompt (inherit from Settings when off)",
    null,
    6,
  );
  form.appendChild(llmSystemPromptW.container);

  const llmUserPromptW: OverridePromptHandle = mountOverridePromptField(
    "Override LLM user prompt (inherit from Settings when off)",
    promptVarsInfoBox(USER_PROMPT_VARS, "User-prompt substitution variables:"),
    6,
  );
  form.appendChild(llmUserPromptW.container);

  const openaiSttPromptW: OverridePromptHandle = mountOverridePromptField(
    "Override OpenAI STT prompt (inherit from Settings when off)",
    promptVarsInfoBox(USER_PROMPT_VARS, "STT-prompt substitution variables:"),
    4,
  );
  form.appendChild(openaiSttPromptW.container);

  // STT-engines override widget — mounted into this host once load()
  // has fetched the preset catalogue. Empty stt_engines on the wire
  // means "inherit from config.stt_presets" (cascade in turn.handler).
  const sttEnginesHost = document.createElement("div");
  form.appendChild(sttEnginesHost);
  let sttEnginesW: OverrideSTTEnginesHandle | null = null;

  const buttonRow = document.createElement("div");
  buttonRow.setAttribute("style", "display: flex; gap: 8px; flex-wrap: wrap; margin-top: 8px;");
  const saveBtn = document.createElement("button");
  saveBtn.type = "submit";
  saveBtn.textContent = "Save";
  saveBtn.setAttribute("style", "padding: 8px 14px; background: #2563eb; color: white; border: 0; border-radius: 6px; cursor: pointer; font-size: 14px;");
  const publishBtn = document.createElement("button");
  publishBtn.type = "button";
  publishBtn.textContent = "Publish";
  publishBtn.setAttribute("style", "padding: 8px 14px; background: #16a34a; color: white; border: 0; border-radius: 6px; cursor: pointer; font-size: 14px;");
  const unpublishBtn = document.createElement("button");
  unpublishBtn.type = "button";
  unpublishBtn.textContent = "Unpublish";
  unpublishBtn.setAttribute("style", "padding: 8px 14px; background: #d97706; color: white; border: 0; border-radius: 6px; cursor: pointer; font-size: 14px;");
  const deleteBtn = document.createElement("button");
  deleteBtn.type = "button";
  deleteBtn.textContent = "Delete";
  deleteBtn.setAttribute("style", "padding: 8px 14px; background: #dc2626; color: white; border: 0; border-radius: 6px; cursor: pointer; font-size: 14px; margin-left: auto;");
  buttonRow.append(saveBtn, publishBtn, unpublishBtn, deleteBtn);
  form.appendChild(buttonRow);

  const publishedIndicator = document.createElement("div");
  publishedIndicator.setAttribute("style", "font-size: 13px; color: #374151; margin-top: 4px;");
  form.appendChild(publishedIndicator);

  let current: AdminGameDTO | null = null;
  let defaultTaskInstructionWidget: SwitchableTextSetHandle | null = null;

  function fillForm(g: AdminGameDTO): void {
    shortCodeInput.value = g.short_code ?? "";
    sequenceInput.value = g.sequence == null ? "" : String(g.sequence);
    titleInput.value = g.title ?? "";
    themeColorInput.value = g.theme_color || "#5E3FBE";
    introPhraseInput.value = g.intro_phrase ?? "";
    defaultMaxTriesInput.value = g.default_max_tries == null ? "" : String(g.default_max_tries);
    llmSystemPromptW.setValue(g.llm_system_prompt);
    llmUserPromptW.setValue(g.llm_user_prompt);
    openaiSttPromptW.setValue(g.openai_stt_prompt);
    descriptionTA.value = g.description ?? "";
    sttEnginesW?.setValue(g.stt_engines ?? []);
    defaultTaskInstructionWidget?.setInitial({
      shortCode: g.default_task_instruction_textset_short_code ?? null,
      members: g.default_task_instruction_members ?? [],
    });
    publishedIndicator.textContent = g.published_at
      ? `Published at ${g.published_at}`
      : "Draft (not published)";
  }

  async function load(): Promise<void> {
    try {
      const [g, presets, textsets] = await Promise.all([
        adminGet<AdminGameDTO>(
          `/api/admin/games/by-short-code/${encodeURIComponent(gameShortCode)}`,
        ),
        getSTTPresets(),
        adminGet<AdminTextSetDTO[]>("/api/admin/textsets"),
      ]);
      current = g;
      sttEnginesW = mountOverrideSTTEnginesField(sttEnginesHost, presets);
      defaultTaskInstructionWidget = mountSwitchableTextSetWidget(defaultTaskInstructionHost, {
        placeholder: "Task instruction variant (LV)",
      });
      const textsetOpts: SwitchableTextSetOption[] = textsets
        .filter((x) => x.short_code)
        .map((x) => ({
          short_code: x.short_code!,
          label: `${x.short_code}: ${x.name ?? "(unnamed)"}`,
        }));
      defaultTaskInstructionWidget.refreshOptions(textsetOpts);
      defaultTaskInstructionWidget.setInitial({
        shortCode: g.default_task_instruction_textset_short_code ?? null,
        members: g.default_task_instruction_members ?? [],
      });
      fillForm(g);
      status.style.display = "none";
      form.style.display = "flex";
      errorBox.style.display = "none";
      cardsContainer.replaceChildren();
      const cardsHeader = document.createElement("h2");
      cardsHeader.textContent = "Cards";
      cardsHeader.setAttribute("style", "font-size: 16px; margin: 0 0 8px 0; color: #111827;");
      cardsContainer.appendChild(cardsHeader);
      if (g.id) {
        renderCardsPanel(cardsContainer, g.id, gameShortCode);
      }
    } catch (err) {
      if (err instanceof AdminError && err.status === 404) {
        status.textContent = `Game “${gameShortCode}” not found.`;
      } else if (err instanceof AdminError && (err.status === 401 || err.status === 403)) {
        status.textContent = "Not signed in — visit /login to authenticate, then refresh.";
      } else {
        status.textContent = `Failed to load: ${(err as Error).message}`;
      }
    }
  }

  async function onSave(e: Event): Promise<void> {
    e.preventDefault();
    if (!current || !current.id) return;
    const patch = diffPatch(
      current,
      shortCodeInput.value,
      sequenceInput.value,
      titleInput.value,
      themeColorInput.value,
      introPhraseInput.value,
      defaultMaxTriesInput.value,
      sttEnginesW?.value() ?? [],
      defaultTaskInstructionWidget?.value() ?? { mode: "non_shared", members: [] },
      llmSystemPromptW.value(),
      llmUserPromptW.value(),
      openaiSttPromptW.value(),
      descriptionTA.value,
    );
    if (Object.keys(patch).length === 0) {
      showAdminToast(document.body, "No changes to save");
      return;
    }
    try {
      const updated = await saveWithSharedEditConfirm<Partial<AdminGameDTO>, AdminGameDTO>(
        `/api/admin/games/${encodeURIComponent(current.id)}`,
        patch,
      );
      current = updated;
      fillForm(updated);
      showAdminToast(document.body, "Saved");
      errorBox.style.display = "none";
      if (updated.short_code && updated.short_code !== gameShortCode) {
        history.replaceState({}, "", `/admin/games/${encodeURIComponent(updated.short_code)}`);
      }
    } catch (err) {
      const msg = err instanceof AdminError
        ? `Save failed (${err.status}): ${err.bodyText.slice(0, 200)}`
        : `Save failed: ${(err as Error).message}`;
      errorBox.textContent = msg;
      errorBox.style.display = "block";
    }
  }

  async function onPublish(): Promise<void> {
    if (!current || !current.id) return;
    try {
      const updated = await adminPost<Record<string, never>, AdminGameDTO>(
        `/api/admin/games/${encodeURIComponent(current.id)}/publish`,
        {},
      );
      current = updated;
      fillForm(updated);
      errorBox.style.display = "none";
      showAdminToast(document.body, "Published");
    } catch (err) {
      if (err instanceof AdminError && err.status === 422) {
        const env = err.jsonBody<AdminPublishError>();
        if (env && Array.isArray(env.errors)) {
          renderPublishErrors(errorBox, env);
          errorBox.style.display = "block";
          return;
        }
      }
      const msg = err instanceof AdminError
        ? `Publish failed (${err.status}): ${err.bodyText.slice(0, 200)}`
        : `Publish failed: ${(err as Error).message}`;
      errorBox.textContent = msg;
      errorBox.style.display = "block";
    }
  }

  async function onUnpublish(): Promise<void> {
    if (!current || !current.id) return;
    try {
      const updated = await adminPost<Record<string, never>, AdminGameDTO>(
        `/api/admin/games/${encodeURIComponent(current.id)}/unpublish`,
        {},
      );
      current = updated;
      fillForm(updated);
      showAdminToast(document.body, "Unpublished");
    } catch (err) {
      const msg = err instanceof AdminError
        ? `Unpublish failed (${err.status}): ${err.bodyText.slice(0, 200)}`
        : `Unpublish failed: ${(err as Error).message}`;
      errorBox.textContent = msg;
      errorBox.style.display = "block";
    }
  }

  async function onDelete(): Promise<void> {
    if (!current || !current.id) return;
    if (!window.confirm(`Delete game ${current.short_code}? This cannot be undone.`)) return;
    try {
      await adminDelete(`/api/admin/games/${encodeURIComponent(current.id)}`);
      showAdminToast(document.body, "Deleted");
      navigateAdmin("/admin/games");
    } catch (err) {
      const msg = err instanceof AdminError
        ? `Delete failed (${err.status}): ${err.bodyText.slice(0, 200)}`
        : `Delete failed: ${(err as Error).message}`;
      errorBox.textContent = msg;
      errorBox.style.display = "block";
    }
  }

  form.addEventListener("submit", onSave);
  publishBtn.addEventListener("click", onPublish);
  unpublishBtn.addEventListener("click", onUnpublish);
  deleteBtn.addEventListener("click", onDelete);

  void load();

  return () => {
    form.removeEventListener("submit", onSave);
    publishBtn.removeEventListener("click", onPublish);
    unpublishBtn.removeEventListener("click", onUnpublish);
    deleteBtn.removeEventListener("click", onDelete);
    defaultTaskInstructionWidget?.unmount();
  };
}
